import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ThemeContext } from '../../../context/ThemeContext';
import { GlobalSet } from '../global/GlobalProvider';

// RM CROWN MARKET - single-file responsive dashboard.
// Existing API contract and navigation destinations are retained.
const API_KEY = 'eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9';
const PLAN_URL = 'https://backoffice.rmcrownmarket.com/public/RM-CrownMarket.pdf';
const TARGET_DATE = '2027-02-15T00:00:00'; // Interpreted in the visitor's local timezone.

const QUICK = [
  /*['Deposit','/deposit','fa-wallet'],*/
  /*['Investment','/community','fa-chart-pie'],*/
  ['To Invite','/affiliate','fa-user-plus'],
  ['Withdraw','/withdraw','fa-money-bill-transfer'],
  /*['Node','/coming-soon','fa-network-wired'],*/
  /*['Convert','/convert-balance','fa-rotate'],*/
  ['Asset','/app-asset','fa-gem'],
  ['Security','/security','fas fa-shield-alt'],
  ['Team Level','/my-smart-team','fa-user'],
  /*['P2P','/balance-transfer','fa-people-arrows'],*/
  ['My Team','/all-team-users','fa-users'],
]; 


const UPCOMING = [
  ['Broker','/coming-soon','fa-briefcase'],
  ['Pay Bill','/coming-soon','fa-file-invoice-dollar'],
  ['Airdrop','/coming-soon','fa-gift'],
  ['Exchanger','/coming-soon','fa-building-columns'],
  ['Social Media','/social-media','fa-share-nodes'],
  ['Coin List','/coming-soon','fa-coins'],
  ['Real Estate','/coming-soon','fa-building'],
  ['Debit Card','/coming-soon','fa-credit-card'],
  ['Cyberspace','/coming-soon','fa-globe'],
  ['NFT','/coming-soon','fa-image'],
];

function Countdown({ targetDate }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const remaining = Math.max(0, new Date(targetDate).getTime() - now);
  const units = [
    ['Days', Math.floor(remaining / 86400000)],
    ['Hours', Math.floor(remaining / 3600000) % 24],
    ['Minutes', Math.floor(remaining / 60000) % 60],
    ['Seconds', Math.floor(remaining / 1000) % 60],
  ];
  return (
    <div className="rcm-countdown" aria-label="Time until Big Update launch">
      {units.map(([label, value]) => (
        <div className="rcm-clock" key={label}>
          <strong>{String(value).padStart(2, '0')}</strong><span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function ServiceGrid({ items }) {
  return (
    <div className="rcm-service-grid">
      {items.map(([title, path, icon]) => (
        <Link key={`${title}-${path}`} to={path} className="rcm-service">
          <span className="rcm-service-icon" aria-hidden="true"><i className={`fa-solid ${icon}`} /></span>
          <span className="rcm-service-title">{title}</span>
          <i className="fa-solid fa-arrow-up-right-from-square rcm-arrow" aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

function BannerSlider({ slides, baseUrl }) {
  const [index, setIndex] = useState(0);
  const safeSlides = Array.isArray(slides) ? slides.filter(s => s && typeof s.img_link === 'string') : [];
  useEffect(() => setIndex(0), [slides]);
  useEffect(() => {
    if (safeSlides.length < 2) return undefined;
    const id = window.setInterval(() => setIndex(v => (v + 1) % safeSlides.length), 6000);
    return () => window.clearInterval(id);
  }, [safeSlides.length]);
  if (!safeSlides.length) return null;
  const slide = safeSlides[index % safeSlides.length];
  const source = /^https?:\/\//i.test(slide.img_link)
    ? slide.img_link
    : `${baseUrl || ''}${slide.img_link}`;
  return (
    <section className="rcm-banner" aria-label="Promotional banners">
      <img key={source} src={source} alt={`RM Crown Market banner ${index + 1}`}
        loading={index === 0 ? 'eager' : 'lazy'} decoding="async"
        fetchPriority={index === 0 ? 'high' : 'auto'} />
      {safeSlides.length > 1 && (
        <div className="rcm-banner-controls">
          <button type="button" aria-label="Previous banner" onClick={() => setIndex(v => (v - 1 + safeSlides.length) % safeSlides.length)}><i className="fa-solid fa-chevron-left" /></button>
          <span>{index + 1} / {safeSlides.length}</span>
          <button type="button" aria-label="Next banner" onClick={() => setIndex(v => (v + 1) % safeSlides.length)}><i className="fa-solid fa-chevron-right" /></button>
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const { changeBackground } = useContext(ThemeContext);
  const { BaseUrl } = GlobalSet();
  const [slides, setSlides] = useState([]);
  const [tradingWallet, setTradingWallet] = useState(null);
  const [tradeStatus, setTradeStatus] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    document.title = 'RM-CrownMarket Home';
    changeBackground({ value: 'lite', label: 'Lite' });
  }, [changeBackground]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        if (active) setRefreshing(true);
        const response = await axios.post('/home-data', { key_s: API_KEY }, { signal: controller.signal });
        if (!active) return;
        const result = response?.data || {};
        setSlides(Array.isArray(result.slider) ? result.slider : []);
        setHomeData(result.data_info ?? null);
        setTradingWallet(result.trading_wallet ?? null);
        setTradeStatus(result.trade_status ?? null);
        if (result.coin_wait != null) localStorage.setItem('coin_wait', String(result.coin_wait));
        setError(false);
      } catch (e) {
        if (active && e?.code !== 'ERR_CANCELED') setError(true);
      } finally {
        if (active) setRefreshing(false);
      }
    };
    load();
    // Preserve the original refresh interval (70,000,000 ms).
    const id = window.setInterval(load, 70000000);
    return () => { active = false; controller.abort(); window.clearInterval(id); };
  }, []);

  const walletLabel = useMemo(() => {
    if (tradingWallet === null || tradingWallet === undefined || tradingWallet === '') return '—';
    const value = Number(tradingWallet);
    return Number.isFinite(value) ? value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : String(tradingWallet);
  }, [tradingWallet]);

  return (
    <main className="rcm-root">
      <style>{`
        :root { --rcm-bg:#00191D; --rcm-cyan:#16B8C7; --rcm-bright:#33D8E2; --rcm-dark:#00252B; --rcm-white:#fff; --rcm-muted:#9AB8BC; --rcm-border:rgba(22,184,199,.22); }
        body { background:#00191D !important; }
        .rcm-root, .rcm-root * { box-sizing:border-box; }
        .rcm-root { background:#00191D; color:#fff; min-height:100vh; width:100%; padding:4px 4px 30px; font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; font-size:14px; line-height:1.5; overflow-x:clip; }
        .rcm-root a { text-decoration:none; }
        .rcm-layout { width:100%; margin:0 auto; }
        .rcm-hero { position:relative; overflow:hidden; border-radius:18px; padding:26px clamp(16px,3vw,40px); background:linear-gradient(115deg,#003039,#00191D 72%); border:1px solid var(--rcm-border); }
        .rcm-hero:after { content:''; position:absolute; width:320px; height:320px; border:1px solid rgba(22,184,199,.18); border-radius:50%; right:-140px; top:-175px; pointer-events:none; }
        .rcm-eyebrow { color:#6FE0E6; letter-spacing:.14em; font-size:11px; font-weight:800; text-transform:uppercase; }
        .rcm-hero h1 { color:#fff; font-size:clamp(23px,3vw,38px); line-height:1.18; letter-spacing:-.035em; margin:9px 0 10px; font-weight:800; }
        .rcm-hero h1 em { color:var(--rcm-cyan); font-style:normal; }
        .rcm-hero p { color:#BDD6D9; margin:0; max-width:650px; }
        .rcm-hero-bottom { display:flex; align-items:center; flex-wrap:wrap; gap:12px; margin-top:21px; }
        .rcm-button { border:0; border-radius:12px; background:linear-gradient(125deg,#16B8C7,#087D8A); color:white !important; display:inline-flex; align-items:center; justify-content:center; gap:9px; padding:11px 19px; font-weight:750; box-shadow:0 5px 16px rgba(22,184,199,.16); transition:transform .18s ease,box-shadow .18s ease; }
        .rcm-button:hover { transform:translateY(-2px); box-shadow:0 9px 20px rgba(22,184,199,.24); }
        .rcm-subtle { color:#9CBEC1; font-size:12px; }
        .rcm-banner { position:relative; margin-top:12px; background:#00252B; border:1px solid var(--rcm-border); border-radius:16px; overflow:hidden; }
        .rcm-banner img { display:block; width:100%; height:auto; max-height:390px; object-fit:cover; aspect-ratio:3.5/1; }
        .rcm-banner-controls { position:absolute; right:10px; bottom:10px; display:flex; align-items:center; gap:9px; padding:5px; border-radius:999px; background:rgba(0,25,29,.83); color:white; font-size:11px; }
        .rcm-banner-controls button { border:0; background:transparent; color:white; cursor:pointer; width:25px; height:25px; border-radius:50%; }
        .rcm-panel { border:1px solid var(--rcm-border); border-radius:17px; padding:clamp(13px,2vw,23px); margin-top:12px; }
        .rcm-panel-dark { background:#00262D; color:white; }
        .rcm-panel-white { background:#fff; color:#092B31; border-color:#DAEBED; }
        .rcm-heading { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:17px; }
        .rcm-heading h2 { color:inherit; font-size:clamp(17px,2vw,21px); line-height:1.3; font-weight:800; margin:0; }
        .rcm-heading p { margin:4px 0 0; font-size:12px; opacity:.65; }
        .rcm-tag { color:#0A8796; font-size:11px; font-weight:800; background:#E6F8FA; border-radius:99px; padding:6px 11px; }
        .rcm-panel-dark .rcm-tag { color:#7CE7EC; background:rgba(22,184,199,.13); }
        .rcm-service-grid { display:grid; grid-template-columns:repeat(6,minmax(0,1fr)); gap:10px; }
        .rcm-service { min-width:0; min-height:104px; position:relative; border:1px solid rgba(22,184,199,.18); background:#00343B; border-radius:13px; padding:15px 9px 11px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; color:#fff !important; transition:transform .2s ease,border-color .2s ease,background .2s ease; }
        .rcm-service:hover { transform:translateY(-3px); background:#00444D; border-color:#16B8C7; }
        .rcm-service-icon { width:42px; height:42px; display:grid; place-items:center; border-radius:12px; background:rgba(22,184,199,.14); color:#42D4DD; font-size:19px; }
        .rcm-service-title { text-align:center; font-size:13px; font-weight:700; line-height:1.25; overflow-wrap:anywhere; }
        .rcm-arrow { position:absolute; top:9px; right:9px; font-size:9px; opacity:.3; }
        .rcm-panel-white .rcm-service { background:#F5FAFB; border-color:#E0ECEE; color:#12343A !important; }
        .rcm-panel-white .rcm-service:hover { background:#EAF8FA; border-color:#16B8C7; }
        .rcm-panel-white .rcm-service-icon { background:#E0F7F9; color:#008B99; }
        .rcm-announce { display:flex; align-items:center; gap:12px; margin-top:12px; padding:13px 17px; background:#fff; color:#15373C; border-radius:13px; border:1px solid #E3EDEF; }
        .rcm-announce i { color:#009BA9; font-size:18px; }
        .rcm-announce span { min-width:0; }
        .rcm-split { display:grid; grid-template-columns:minmax(0,1.2fr) minmax(260px,.8fr); gap:12px; align-items:stretch; margin-top:12px; }
        .rcm-split .rcm-panel { margin-top:0; }
        .rcm-countdown { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; margin-top:17px; }
        .rcm-clock { text-align:center; background:#F0F9FA; border:1px solid #DCEDEF; border-radius:11px; padding:12px 4px; }
        .rcm-clock strong { display:block; color:#087E8B; font-size:clamp(19px,2.3vw,29px); font-variant-numeric:tabular-nums; line-height:1.2; }
        .rcm-clock span { color:#6D898D; font-size:11px; }
        .rcm-overview { display:flex; flex-direction:column; gap:12px; }
        .rcm-stat { border:1px solid rgba(22,184,199,.23); background:#00343B; border-radius:12px; padding:14px 16px; }
        .rcm-stat-label { color:#9EC1C5; font-size:12px; }
        .rcm-stat-value { font-size:24px; font-weight:800; margin-top:3px; color:#fff; overflow-wrap:anywhere; }
        .rcm-status { display:inline-flex; align-items:center; gap:7px; margin-top:5px; color:#8FD9DF; font-size:12px; }
        .rcm-dot { width:7px; height:7px; background:#16B8C7; border-radius:50%; }
        .rcm-bottom { text-align:center; color:#84A8AD; font-size:12px; padding:22px 4px 5px; }
        .rcm-error { color:#FFD2D2; font-size:12px; }
        .rcm-root :focus-visible { outline:2px solid #16B8C7; outline-offset:3px; }
        @media (max-width:1100px) { .rcm-service-grid { grid-template-columns:repeat(5,minmax(0,1fr)); } }
        @media (max-width:800px) { .rcm-service-grid { grid-template-columns:repeat(4,minmax(0,1fr)); } .rcm-split { grid-template-columns:1fr; } }
        @media (max-width:520px) { .rcm-root { padding:4px 4px 22px; } .rcm-hero { padding:22px 15px; border-radius:14px; } .rcm-service-grid { grid-template-columns:repeat(3,minmax(0,1fr)); gap:7px; } .rcm-service { min-height:95px; padding:11px 5px; } .rcm-service-icon { width:37px; height:37px; font-size:17px; } .rcm-service-title { font-size:12px; } .rcm-panel { border-radius:14px; padding:13px 9px; } .rcm-banner img { aspect-ratio:2/1; } .rcm-clock { padding:10px 2px; } .rcm-announce { padding:12px; } }
        @media (max-width:350px) { .rcm-service-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (prefers-reduced-motion:reduce) { .rcm-root *, .rcm-root *:before, .rcm-root *:after { animation:none !important; transition:none !important; } }
      `}</style>

      <div className="rcm-layout">
        <section className="rcm-hero">
          <div className="rcm-eyebrow">RM CROWN MARKET · Member Dashboard</div>
          <h1>Welcome to Your <em>Business Hub</em></h1>
          <p>Manage your account, explore available services, and stay connected with your business network.</p>
          <div className="rcm-hero-bottom">
            <Link className="rcm-button" to="/affiliate"><i className="fa-solid fa-chart-line" /> To Invite</Link>
            <span className="rcm-subtle">Simple access · Responsive experience</span>
          </div>
        </section>

        <BannerSlider slides={slides} baseUrl={BaseUrl} />

        <section className="rcm-panel rcm-panel-dark">
          <div className="rcm-heading"><div><h2>Quick Services</h2><p>Everything you need in one place</p></div><span className="rcm-tag">Smart Access</span></div>
          <ServiceGrid items={QUICK} />
          <div style={{ marginTop: 10 }}>
            <a className="rcm-button" href={PLAN_URL} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-file-pdf" /> View Plan PDF</a>
          </div>
        </section>

        <div className="rcm-announce"><i className="fa-solid fa-bullhorn" aria-hidden="true" /><span>Exciting new features and services are on the way. Stay tuned!</span></div>

        <div className="rcm-split">
          <section className="rcm-panel rcm-panel-white">
            <div className="rcm-heading"><div><h2>New Project — Coming Soon</h2><p>Scheduled countdown to December 15, 2026</p></div><span className="rcm-tag">Countdown</span></div>
            <Countdown targetDate={TARGET_DATE} />
          </section>
          <section className="rcm-panel rcm-panel-dark">
            <div className="rcm-heading"><div><h2>Account Snapshot</h2><p>Values received from your account API</p></div><span className="rcm-tag">Overview</span></div>
            <div className="rcm-overview">
              <div className="rcm-stat"><div className="rcm-stat-label">Trading Wallet</div><div className="rcm-stat-value">{walletLabel}</div></div>
              <div className="rcm-stat"><div className="rcm-stat-label">Trading Status</div><div className="rcm-status"><span className="rcm-dot" />{tradeStatus === null ? 'Awaiting data' : Number(tradeStatus) === 1 ? 'Active' : 'Inactive'}</div></div>
              {error && <span className="rcm-error">Account data could not be refreshed. Please try again later.</span>}
            </div>
          </section>
        </div>

        {/*<section className="rcm-panel rcm-panel-white">
          <div className="rcm-heading"><div><h2>Coming Soon</h2><p>Discover upcoming platform features</p></div><span className="rcm-tag">New Opportunities</span></div>
          <ServiceGrid items={UPCOMING} />
        </section>*/}
        <footer className="rcm-bottom">RM CROWN MARKET · Empowering Business, Connecting Opportunities, Building the Future.</footer>
      </div>
    </main>
  );
}
